import merge from "lodash/merge";
import { Action, Reducer } from "redux";

// -- Need to support the "unknown" type because the platform sometimes uses "as any" when combining reducers.
function mergeReducer<State, BaseActions extends Action, ExtendedActions extends Action, U = unknown>(
    baseReducer: Reducer<U, BaseActions>,
    extendedReducer: Reducer<State, ExtendedActions>,
): Reducer<U, BaseActions | ExtendedActions>;
// -----------------------------------------------------------------------------------------------------------

function mergeReducer<State, BaseActions extends Action, ExtendedActions extends Action>(
    baseReducer: Reducer<State, BaseActions>,
    extendedReducer: Reducer<State, ExtendedActions>,
) {
    return (state: State, action: BaseActions | ExtendedActions): State => {
        const baseState = baseReducer(state, action as BaseActions);
        const extensionState = extendedReducer(state, action as ExtendedActions);

        if (!state) {
            // The initial state is not defined yet, we merge both results to enable all initial values.
            return merge({}, baseState, extensionState);
        }

        const baseStateHasChanged = baseState !== state;
        const extensionStateHasChanged = extensionState !== state;

        if (baseStateHasChanged && extensionStateHasChanged) {
            // If both states have changed, the native action was overriden by the extension, we return the extension result only.
            return extensionState;
        }

        if (baseStateHasChanged) {
            // If only the base state has changed, we look for an extend action in the extended reducer.
            // The reducer directly returns the extended state (if existing) or the unmodified base state (if not existing).
            return extendedReducer(baseState, {
                ...(action as ExtendedActions),
                type: `After-${action.type}`,
            });
        }

        if (extensionStateHasChanged) {
            // If only the extension state changed, the action is not native, we can safely return the extension result.
            return extensionState;
        }

        // Nothing has changed, return the original state.
        return state;
    };
}

export default mergeReducer;
